import React from 'react';
import Badge from '../ui/Badge';
import { relativeTime } from '../domainMetrics';
import {
  senderName, snippet, spamState, spamBucket, domainLabel, isFeedback, tracksHandled,
} from '../emailHelpers';

/**
 * The inbox list pane. Rows are buttons-in-divs: the row itself selects,
 * the checkbox is for bulk actions only.
 *
 * <EmailList emails selectedId cursorId seenIds selectedIds showDomain onOpen onToggleSelect onToggleAll />
 *
 * `selectedId` is the message the reader is showing; `cursorId` is where the
 * keyboard is, which is only a hint until Enter opens it. They are drawn
 * differently so the list never claims to have opened something it hasn't.
 */
export default function EmailList({
  emails,
  selectedId,
  cursorId,
  seenIds,
  selectedIds,
  showDomain,
  onOpen,
  onToggleSelect,
  onToggleAll,
  listRef,
}) {
  const allSelected = emails.length > 0 && emails.every((e) => selectedIds.has(String(e.id)));

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        className="card-header"
        style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)', marginBottom: 0 }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', margin: 0 }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleAll}
            aria-label={allSelected ? 'Deselect all' : 'Select all'}
            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
          />
          <span className="muted">{emails.length} shown</span>
        </label>
      </div>

      <ul
        ref={listRef}
        style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '70vh', overflowY: 'auto' }}
      >
        {emails.map((email) => {
          const id = String(email.id);
          const isActive = selectedId === id;
          const isCursor = !isActive && cursorId === id;
          const isChecked = selectedIds.has(id);
          const unread = !seenIds.has(id);
          return (
            <li
              key={id}
              id={`email-row-${id}`}
              aria-current={isActive ? 'true' : undefined}
              style={{
                display: 'flex',
                gap: '10px',
                padding: '10px 12px',
                borderTop: '1px solid var(--color-border)',
                cursor: 'pointer',
                background: isActive ? '#eff6ff' : isChecked ? 'var(--color-bg)' : undefined,
                borderLeft: isActive ? '3px solid var(--color-primary)' : '3px solid transparent',
                outline: isCursor ? '2px solid var(--color-border)' : 'none',
                outlineOffset: '-2px',
              }}
              onClick={() => onOpen(id)}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggleSelect(id)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select message from ${senderName(email.sender)}`}
                style={{ width: '15px', height: '15px', marginTop: '3px', cursor: 'pointer', accentColor: 'var(--color-primary)', flexShrink: 0 }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{
                    fontWeight: unread ? 700 : 500,
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}>
                    {unread && <span aria-label="Unread" title="Unread" style={{ color: 'var(--color-primary)' }}>• </span>}
                    {senderName(email.sender)}
                  </span>
                  <span className="muted small" style={{ flexShrink: 0 }}>{relativeTime(email.created_at)}</span>
                </div>
                <div style={{
                  fontWeight: unread ? 700 : 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {email.subject || '(no subject)'}
                </div>
                <div className="muted small" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {snippet(email.body_text || email.body_html)}
                </div>
                {(showDomain || spamBucket(email.spam_score) === 'spam' || tracksHandled(email)) && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                    {tracksHandled(email) && (
                      <Badge state={email.handled ? 'info' : email.handled === false ? 'danger' : 'warn'}>
                        {email.handled ? 'Handled' : isFeedback(email) ? 'Reader feedback' : 'Contact form'}
                      </Badge>
                    )}
                    {showDomain && email.project_id && (
                      <Badge state="info" title={email.project_id}>
                        <span style={{ textTransform: 'none' }}>{domainLabel(email.project_id)}</span>
                      </Badge>
                    )}
                    {spamBucket(email.spam_score) === 'spam' && (
                      <Badge state={spamState(email.spam_score)}>Spam {String(email.spam_score)}</Badge>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
